/**
 * Space add/remove guard + resolution helpers — ONE source of truth shared
 * by the shell reducer (`shellUiReducer.ts` — audit + shell-state arm), the
 * geometry reducer (`geometryReducerLayout.ts` — document mutation arm) and
 * the command-line router (`routeLayoutCommand` in `editorStore.ts` +
 * `resolveGeometryDocumentCommand` in `storeHelpers.ts`).
 *
 * Both reducer arms run independently inside one dispatch (shell first,
 * geometry second) — they MUST accept/reject identically or the resync
 * desynchronizes the audit trail from the document. Centralising the
 * predicates here guarantees that.
 *
 * AutoCAD parity rules implemented (LAYOUT command, Autodesk reference;
 * `ezdxf` `Layouts.new()` / `Layouts.delete()` mirror the same contract):
 *   - layout tab names are unique CASE-INSENSITIVELY;
 *   - a drawing holds at most 255 paper-space layouts;
 *   - the Model tab cannot be deleted (`Layouts.delete(): "Model space
 *     can not be deleted."`);
 *   - the LAST remaining paper-space layout cannot be deleted (AutoCAD
 *     keeps >= 1 layout; ezdxf: "Can not delete last layout.");
 *   - deleting the ACTIVE layout activates an adjacent remaining layout
 *     (next tab in order, else the previous one);
 *   - LAYOUT New default names follow "Layout<N>" with the next free N
 *     (the existing-tab naming convention is mirrored EXACTLY — this
 *     code base seeds "Layout 1"/"Layout 2", so the default base keeps
 *     the space separator; a drawing whose tabs follow the RU
 *     "Лист<N>" convention gets "Лист <N>"-style defaults).
 *
 * The functions are pure and operate on a minimal structural space shape
 * so the contracts-level shell `DrawingSpace` and the drawing-model
 * `DrawingSpace` both satisfy them.
 */

export interface SpaceLike {
  id: string;
  name: string;
  kind: string;
}

/** AutoCAD caps a drawing at 255 paper-space layouts. */
export const MAX_PAPER_LAYOUTS = 255;

/**
 * AutoCAD caps a layout tab name at 255 characters (same ceiling as
 * other symbol-table names since AutoCAD 2000 extended symbol names).
 * Longer names are rejected up front — forwarding them into the
 * synthetic command would fail deeper in the dispatch or truncate
 * silently (Codex P2-4).
 */
export const MAX_LAYOUT_NAME_LENGTH = 255;

/** True when a trimmed layout name exceeds the AutoCAD 255-char cap. */
export function isLayoutNameTooLong(name: string): boolean {
  return name.trim().length > MAX_LAYOUT_NAME_LENGTH;
}

/**
 * ONE source of truth for the case-insensitive layout-name collision
 * check (AutoCAD layout tab names are unique CASE-INSENSITIVELY).
 * `excludeSpaceId` skips the space being renamed so a pure case change
 * of a tab's OWN name ("Sheet" → "SHEET") stays legal — AutoCAD permits
 * a case-only rename of the tab itself (Codex P2-2).
 */
export function spaceNameTakenCI(
  spaces: readonly SpaceLike[],
  name: string,
  excludeSpaceId?: string,
): boolean {
  const requestedName = name.trim().toLowerCase();
  return spaces.some(
    (space) =>
      space.id !== excludeSpaceId &&
      space.name.trim().toLowerCase() === requestedName,
  );
}

export type AddSpaceRejection =
  | "duplicate-name"
  | "duplicate-id"
  | "limit"
  | "name-too-long";

/**
 * Validate a LAYOUT NEW request against the current space list. Returns
 * `null` when the add is allowed, otherwise the rejection reason.
 */
export function validateAddSpace(
  spaces: readonly SpaceLike[],
  request: { spaceId: string; name: string },
): AddSpaceRejection | null {
  if (isLayoutNameTooLong(request.name)) {
    return "name-too-long";
  }
  if (spaceNameTakenCI(spaces, request.name)) {
    return "duplicate-name";
  }
  if (spaces.some((space) => space.id === request.spaceId)) {
    return "duplicate-id";
  }
  const layoutCount = spaces.filter((space) => space.kind === "layout").length;
  if (layoutCount >= MAX_PAPER_LAYOUTS) {
    return "limit";
  }
  return null;
}

export type RemoveSpaceRejection =
  | "model"
  | "not-found"
  | "not-layout"
  | "last-layout";

/**
 * Validate a LAYOUT DELETE request against the current space list.
 * Returns `null` when the removal is allowed, otherwise the rejection
 * reason.
 */
export function validateRemoveSpace(
  spaces: readonly SpaceLike[],
  spaceId: string,
): RemoveSpaceRejection | null {
  if (spaceId === "model") return "model";
  const target = spaces.find((space) => space.id === spaceId);
  if (!target) return "not-found";
  if (target.kind !== "layout") return "not-layout";
  const layoutCount = spaces.filter((space) => space.kind === "layout").length;
  if (layoutCount <= 1) return "last-layout";
  return null;
}

export type RenameSpaceRejection =
  | "model"
  | "not-found"
  | "not-layout"
  | "empty"
  | "name-too-long"
  | "duplicate-name";

/**
 * Validate a LAYOUT RENAME request against the current space list —
 * ONE source of truth shared by the shell arm (`shellUiReducer.ts`),
 * the geometry-document arm (`geometryReducerLayout.ts`) and the
 * command-line LAYOUT R router (App.tsx), so all arms accept/reject
 * identically (Codex P2-2 — the arms previously duplicated the guard
 * list with a case-SENSITIVE duplicate check, letting "Layout 2" →
 * "layout 1" mint a CI-duplicate tab).
 *
 * AutoCAD parity: Model cannot be renamed; the new name must be
 * non-empty, ≤255 chars and CI-unique among the OTHER tabs (a pure
 * case change of the tab's own name is allowed).
 */
export function validateRenameSpace(
  spaces: readonly SpaceLike[],
  spaceId: string,
  newName: string,
): RenameSpaceRejection | null {
  if (spaceId === "model") return "model";
  const target = spaces.find((space) => space.id === spaceId);
  if (!target) return "not-found";
  if (target.kind !== "layout") return "not-layout";
  const trimmedName = newName.trim();
  if (!trimmedName) return "empty";
  if (isLayoutNameTooLong(trimmedName)) return "name-too-long";
  if (spaceNameTakenCI(spaces, trimmedName, spaceId)) {
    return "duplicate-name";
  }
  return null;
}

/**
 * AutoCAD behaviour when the ACTIVE layout tab is deleted: the adjacent
 * remaining layout becomes current — the NEXT layout tab in order when
 * one exists, otherwise the PREVIOUS one. Callers must have validated
 * the removal first (the last-layout guard guarantees a sibling
 * exists). Falls back to "model" defensively if no sibling remains.
 */
export function resolveAdjacentLayoutId(
  spaces: readonly SpaceLike[],
  removedSpaceId: string,
): string {
  const layouts = spaces.filter((space) => space.kind === "layout");
  const removedIndex = layouts.findIndex(
    (space) => space.id === removedSpaceId,
  );
  if (removedIndex === -1) {
    return layouts[0]?.id ?? "model";
  }
  const next = layouts[removedIndex + 1] ?? layouts[removedIndex - 1];
  return next?.id ?? "model";
}

/**
 * ONE source of truth for resolving a user-typed tab name against a
 * candidate space list: exact (case-sensitive) match first, then a
 * forgiving case-insensitive fallback (AutoCAD layout-name lookups are
 * case-insensitive).
 *
 * DETERMINISM on an ambiguous name: DXF itself does not forbid two tabs
 * whose names differ only by case — `dxfBridge.spaces.ts`'s
 * `disambiguate()` de-duplicates CASE-SENSITIVELY, so an imported
 * drawing really can carry both "Sheet" and "SHEET". The two-pass order
 * makes the winner deterministic and predictable: an EXACT-case hit
 * always beats a case-only match, and among several case-only matches
 * the FIRST in tab order wins (`spaces` arrives sorted by the LAYOUT
 * `tabOrder` group code).
 */
function matchSpaceByName<T extends SpaceLike>(
  candidates: readonly T[],
  requestedName: string,
): T | undefined {
  const needle = requestedName.trim();
  const needleLower = needle.toLowerCase();
  return (
    candidates.find((space) => space.name === needle) ??
    candidates.find((space) => space.name.toLowerCase() === needleLower)
  );
}

/**
 * Resolve a user-typed layout name to a PAPER-SPACE layout, mirroring
 * the LAYOUT SET resolver: exact (case-sensitive) match first, then a
 * forgiving case-insensitive fallback (AutoCAD layout-name lookups are
 * case-insensitive). The Model space and synthetic block-edit/refedit
 * spaces are never returned.
 */
export function findLayoutSpaceByName<T extends SpaceLike>(
  spaces: readonly T[],
  requestedName: string,
): T | undefined {
  return matchSpaceByName(
    spaces.filter((space) => space.kind === "layout"),
    requestedName,
  );
}

/**
 * The tabs a user can make CURRENT: the Model tab plus every
 * paper-space layout — exactly the set the layout tab strip renders and
 * the set the bare `LAYOUT` command lists.
 *
 * The model check is anchored to `id === "model"` rather than
 * `kind === "model"` because `buildShellSpacesFromGeometryDocument`
 * projects synthetic block-edit / refedit spaces to `kind: "model"`;
 * those are entered through BEDIT/REFEDIT, never through a tab or the
 * LAYOUT command, so they must not appear here.
 */
export function listSwitchableSpaces<T extends SpaceLike>(
  spaces: readonly T[],
): T[] {
  return spaces.filter(
    (space) => space.id === "model" || space.kind === "layout",
  );
}

/**
 * Resolve a user-typed tab name for a LAYOUT space SWITCH (AutoCAD's
 * default `<Set>` option) against the switchable tabs.
 *
 * Resolution order — NAME first (that is what the user sees on the tab
 * and what bare `LAYOUT` lists), then the internal space ID:
 *   1. exact name          — "АБИ 1"        -> layout:АБИ 1
 *   2. case-insensitive    — "абИ 1"        -> layout:АБИ 1
 *   3. exact id            — "layout:КРОНА" -> layout:КРОНА
 *   4. case-insensitive id — "LAYOUT-1"     -> layout-1
 *
 * The id passes exist because runtime-created layouts use "layout-<N>"
 * ids (`mintLayoutSpaceId`) while imported layouts use
 * "layout:<name>" ids that PRESERVE the DXF tab name's case
 * (`dxfBridge.spaces.ts`) — so an id is never `name.toLowerCase()` and
 * must be matched, never derived. Keeping the id passes LAST also
 * preserves the historical `LAYOUT LAYOUT-1` (by-id) command form
 * without letting an id shadow a real tab NAME.
 */
export function findSwitchableSpaceByName<T extends SpaceLike>(
  spaces: readonly T[],
  requestedName: string,
): T | undefined {
  const candidates = listSwitchableSpaces(spaces);
  const needle = requestedName.trim();
  const needleLower = needle.toLowerCase();
  return (
    matchSpaceByName(candidates, needle) ??
    candidates.find((space) => space.id === needle) ??
    candidates.find((space) => space.id.toLowerCase() === needleLower)
  );
}

/**
 * AutoCAD's LAYOUT option keywords —
 * `[Copy/Delete/New/Template/Rename/SAveas/Set/?]` plus the accepted
 * abbreviations, and the RU sub-keywords this code base already
 * recognises (SET: УСТАН/У, NEW: НОВЫЙ/НОВ/Н, DELETE: УДАЛИТЬ/УДАЛ/УД,
 * RENAME: ПЕРЕИМ/П).
 *
 * `parseLayoutSwitchText` uses this set to keep an OPTION submission
 * (`LAYOUT R Old New`, `LAYOUT ?`, an unimplemented `LAYOUT T ...`)
 * from being swallowed as a tab NAME. AutoCAD resolves the option
 * prompt the same way — a keyword always wins over a same-spelled
 * layout name, and such a tab stays reachable through the explicit
 * `LAYOUT SET <name>` form.
 *
 * COPY / TEMPLATE / SAVEAS are listed but NOT implemented (see
 * `docs/test-gaps/cmd-layout.md`); listing them here means those
 * submissions keep falling through to the existing
 * `audit.command_not_wired` feedback instead of being misreported as an
 * unknown tab name.
 */
const LAYOUT_OPTION_KEYWORDS = new Set<string>([
  // EN — Autodesk LAYOUT prompt keywords + abbreviations.
  "?",
  "C",
  "COPY",
  "D",
  "DEL",
  "DELETE",
  "N",
  "NEW",
  "R",
  "RENAME",
  "S",
  "SA",
  "SAVEAS",
  "SET",
  "T",
  "TEMPLATE",
  // RU — the sub-keywords the EN/RU parsers in this code base accept.
  "Н",
  "НОВ",
  "НОВЫЙ",
  "П",
  "ПЕРЕИМ",
  "У",
  "УД",
  "УДАЛ",
  "УДАЛИТЬ",
  "УСТАН",
]);

/** True when a token is a LAYOUT option keyword rather than a tab name. */
export function isLayoutOptionKeyword(token: string): boolean {
  return LAYOUT_OPTION_KEYWORDS.has(token.trim().toUpperCase());
}

/**
 * Command-line parse for a bare `LAYOUT <name>` submission — AutoCAD's
 * DEFAULT option at the LAYOUT prompt
 * (`[Copy/Delete/New/Template/Rename/SAveas/Set/?] <set>:`), i.e. "make
 * this tab current".
 *
 * Returns the raw requested tab name (layout names may contain spaces
 * and non-Latin characters — "АБИ 1", "КРОНА"), or `null` when the text
 * is not a LAYOUT submission at all or when its first token is an
 * OPTION keyword (those belong to the dedicated SET / NEW / DELETE /
 * RENAME handlers).
 */
export function parseLayoutSwitchText(text: string): string | null {
  const match = /^\s*(?:-?LAYOUT|МАКЕТ|РАЗМЛИСТ)\s+(.+?)\s*$/iu.exec(text);
  const requestedName = match?.[1]?.trim();
  if (!requestedName) return null;
  const firstToken = requestedName.split(/\s+/u)[0] ?? "";
  if (isLayoutOptionKeyword(firstToken)) return null;
  return requestedName;
}

/**
 * Default tab name for LAYOUT New when the user supplies none —
 * AutoCAD proposes "Layout<N>" with the next free N. The base + the
 * separator mirror the EXISTING tab convention exactly: this code base
 * seeds "Layout 1" / "Layout 2" (space-separated), and a drawing whose
 * tabs follow the RU "Лист<N>" convention gets "Лист"-based defaults.
 * N starts past the highest numbered tab and bumps until the name is
 * unique case-insensitively.
 */
export function nextDefaultLayoutName(spaces: readonly SpaceLike[]): string {
  const layouts = spaces.filter((space) => space.kind === "layout");
  // Detect the established convention. Default: "Layout" + space.
  const ruPattern = /^Лист\s?(\d+)$/iu;
  const enPattern = /^Layout\s?(\d+)$/i;
  const ruMatches = layouts.filter((s) => ruPattern.test(s.name));
  const enMatches = layouts.filter((s) => enPattern.test(s.name));
  const useRu = ruMatches.length > 0 && enMatches.length === 0;
  const pattern = useRu ? ruPattern : enPattern;
  const matches = useRu ? ruMatches : enMatches;
  // Mirror the separator of the existing tabs ("Layout 1" vs "Layout1");
  // seed convention in this code base is space-separated.
  const separatorSample = matches[0]?.name;
  const separator =
    separatorSample !== undefined && !/\s/.test(separatorSample) ? "" : " ";
  const base = useRu ? "Лист" : "Layout";
  let n = 0;
  for (const space of matches) {
    const matched = pattern.exec(space.name);
    const value = matched?.[1] !== undefined ? Number(matched[1]) : 0;
    if (Number.isFinite(value) && value > n) n = value;
  }
  // Also consider the layout COUNT so a drawing with renamed tabs still
  // proposes a sensible "<base> <count+1>" (AutoCAD's <Layout3> default
  // on a 2-layout drawing).
  if (layouts.length > n) n = layouts.length;
  let candidate = `${base}${separator}${n + 1}`;
  while (spaceNameTakenCI(spaces, candidate)) {
    n += 1;
    candidate = `${base}${separator}${n + 1}`;
  }
  return candidate;
}

/**
 * Mint a fresh space id for a runtime-created layout. Runtime spaces use
 * the "layout-<N>" id convention (seed document: layout-1 / layout-2);
 * imported documents use "layout:<name>" ids, which never collide with
 * the dash-form. First free N wins.
 */
export function mintLayoutSpaceId(spaces: readonly SpaceLike[]): string {
  const used = new Set(spaces.map((space) => space.id));
  let n = 1;
  while (used.has(`layout-${n}`)) n += 1;
  return `layout-${n}`;
}

/**
 * Command-line parse for the LAYOUT New / Delete sub-commands (EN + RU
 * command names and option keywords).
 *
 * Keyword tokens:
 *   - NEW:    `NEW` / `N` (EN), `НОВЫЙ` / `НОВ` / `Н` (RU — mirrors the
 *     LAYOUT RENAME convention of accepting the option's lead letter,
 *     `ПЕРЕИМ`/`П`).
 *   - DELETE: `DELETE` / `DEL` / `D` (EN), `УДАЛИТЬ` / `УДАЛ` / `УД`
 *     (RU). The bare `У` abbreviation is NOT accepted for Delete: this
 *     code base shipped `У` as the LAYOUT SET (`УСТАН`) abbreviation
 *     first, and silently re-pointing a one-letter token from
 *     "activate" to a DESTRUCTIVE delete would be a trap. `УД` is the
 *     shortest unambiguous Delete token.
 *
 * Returns `null` when the text is not a LAYOUT New/Delete submission.
 * The optional name capture is the raw remainder (layout names may
 * contain spaces).
 */
export function parseLayoutMutationText(
  text: string,
): { op: "new" | "delete"; name: string | null } | null {
  const newMatch =
    /^\s*(?:-?LAYOUT|МАКЕТ|РАЗМЛИСТ)\s+(?:NEW|N|НОВЫЙ|НОВ|Н)(?:\s+(.+?))?\s*$/iu.exec(
      text,
    );
  if (newMatch) {
    return { op: "new", name: newMatch[1]?.trim() || null };
  }
  const deleteMatch =
    /^\s*(?:-?LAYOUT|МАКЕТ|РАЗМЛИСТ)\s+(?:DELETE|DEL|D|УДАЛИТЬ|УДАЛ|УД)(?:\s+(.+?))?\s*$/iu.exec(
      text,
    );
  if (deleteMatch) {
    return { op: "delete", name: deleteMatch[1]?.trim() || null };
  }
  return null;
}

export type LayoutMutationResolution =
  | {
      ok: true;
      command:
        | {
            type: "shell/add-space";
            spaceId: string;
            name: string;
            kind: "layout";
          }
        | { type: "shell/remove-space"; spaceId: string };
    }
  | { ok: false; errorKey: "notfound" | "model" | "toolong"; name: string };

/**
 * Resolve a parsed LAYOUT New/Delete submission into the typed shell
 * command — DETERMINISTICALLY, so the two dispatch arms that parse the
 * same text against the same pre-dispatch state (`routeLayoutCommand`
 * for the shell side, `resolveGeometryDocumentCommand` for the
 * geometry side) mint the SAME space id / resolve the SAME target.
 *
 * Only name resolution failures are reported here (the reducers cannot
 * act without a target id). Every other guard (duplicate name, layout
 * cap, Model/last-layout protection) lives in `validateAddSpace` /
 * `validateRemoveSpace`, evaluated INSIDE both reducers.
 */
export function resolveLayoutMutationCommand(
  spaces: readonly SpaceLike[],
  activeSpaceId: string,
  parsed: { op: "new" | "delete"; name: string | null },
): LayoutMutationResolution {
  if (parsed.op === "new") {
    // Codex P2-4 — reject an overlong name HERE, before the synthetic
    // command is minted: forwarding it raw would fail deeper inside the
    // recursive dispatch (or truncate silently). AutoCAD caps layout
    // names at 255 characters. The reducer-side `validateAddSpace`
    // repeats the check for the typed-command arm.
    if (parsed.name !== null && isLayoutNameTooLong(parsed.name)) {
      return { ok: false, errorKey: "toolong", name: parsed.name };
    }
    const name = parsed.name ?? nextDefaultLayoutName(spaces);
    return {
      ok: true,
      command: {
        type: "shell/add-space",
        spaceId: mintLayoutSpaceId(spaces),
        name,
        kind: "layout",
      },
    };
  }
  // DELETE — explicit name wins; AutoCAD's prompt default is the CURRENT
  // layout, so a bare `LAYOUT D` targets the active tab.
  if (parsed.name !== null) {
    // AutoCAD's Model guard fires by NAME too ("Model" is not a deletable
    // tab) — report the dedicated message instead of a generic not-found.
    if (parsed.name.toLowerCase() === "model") {
      return { ok: false, errorKey: "model", name: parsed.name };
    }
    const target = findLayoutSpaceByName(spaces, parsed.name);
    if (!target) {
      return { ok: false, errorKey: "notfound", name: parsed.name };
    }
    return {
      ok: true,
      command: { type: "shell/remove-space", spaceId: target.id },
    };
  }
  if (activeSpaceId === "model") {
    return { ok: false, errorKey: "model", name: "Model" };
  }
  return {
    ok: true,
    command: { type: "shell/remove-space", spaceId: activeSpaceId },
  };
}
